import { useEffect, useMemo, useState, KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { differenceInCalendarDays } from 'date-fns'
import { AppDispatch, RootState } from 'store'
import {
  checkOut,
  getAccommodationStatistics,
  getBedsHistory,
} from 'features/accommodation/accommodationSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { IGetAllBedsHiistorySingleResponse } from 'types/api/accommodationApi.interface'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { JustSpinner } from 'components/Loading/Loading'
import { Input } from 'components/Input/Input'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Box, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { formatDateForDisplay } from 'utils/helpers'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { ExclamationMarkIcon } from '../icons/ExclamationMarkIcon'
import { PersonIcon } from '../icons/PersonIcon'
import { SearchField } from '../Accommodation.style'
import {
  StatBand,
  FilterBar,
  ActionWrap,
  FilterField,
  FilterLabel,
  PersonCell,
  PersonName,
  LocationCell,
  DateCell,
  OverdueTag,
  BedTag,
  LoaderWrap,
} from './CheckOut.styled'

type OccupancyFilter = 'all' | 'today' | 'overdue'

const occupantName = (o: IGetAllBedsHiistorySingleResponse) => {
  if (o.user) return `${o.user.firstName} ${o.user.lastName}`
  if (o.accommodationGuest)
    return `${o.accommodationGuest.firstName} ${o.accommodationGuest.lastName}`
  return 'Unknown'
}

const isOverdue = (o: IGetAllBedsHiistorySingleResponse) =>
  !!o.expectedCheckOutTimestamp &&
  new Date(o.expectedCheckOutTimestamp).getTime() < Date.now()

const overdueDays = (o: IGetAllBedsHiistorySingleResponse) => {
  const days = differenceInCalendarDays(new Date(), new Date(o.expectedCheckOutTimestamp))
  return days < 1 ? 1 : days
}

const isDueToday = (o: IGetAllBedsHiistorySingleResponse) =>
  !!o.expectedCheckOutTimestamp &&
  differenceInCalendarDays(new Date(o.expectedCheckOutTimestamp), new Date()) === 0

export const CheckOut = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { bedsHistory, isLoading } = useSelector((state: RootState) => state.accomodation)

  const [filter, setFilter] = useState<OccupancyFilter>('all')
  const [beforeDate, setBeforeDate] = useState<Date | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [query, setQuery] = useState('')

  const refetch = () => {
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
    dispatch(getAccommodationStatistics({ page: 1, limit: 1 }))
  }

  useEffect(() => {
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const current = useMemo(
    () => (bedsHistory?.data || []).filter((o) => !o.actualCheckOutTimestamp),
    [bedsHistory],
  )

  const overdueCount = useMemo(() => current.filter(isOverdue).length, [current])
  const dueTodayCount = useMemo(() => current.filter(isDueToday).length, [current])

  const rows = useMemo(() => {
    let list = current

    if (filter === 'overdue') list = list.filter(isOverdue)
    else if (filter === 'today') list = list.filter(isDueToday)

    if (beforeDate) {
      const cutoff = new Date(
        beforeDate.getFullYear(),
        beforeDate.getMonth(),
        beforeDate.getDate(),
        23,
        59,
        59,
        999,
      ).getTime()
      list = list.filter(
        (o) =>
          o.expectedCheckOutTimestamp &&
          new Date(o.expectedCheckOutTimestamp).getTime() <= cutoff,
      )
    }

    if (query) {
      const q = query.toLowerCase()
      list = list.filter(
        (o) =>
          occupantName(o).toLowerCase().includes(q) ||
          (o.bed?.label || '').toLowerCase().includes(q),
      )
    }

    return [...list].sort((a, b) => {
      const aOver = isOverdue(a)
      const bOver = isOverdue(b)
      if (aOver !== bOver) return aOver ? -1 : 1
      const aOut = a.expectedCheckOutTimestamp
        ? new Date(a.expectedCheckOutTimestamp).getTime()
        : Number.MAX_SAFE_INTEGER
      const bOut = b.expectedCheckOutTimestamp
        ? new Date(b.expectedCheckOutTimestamp).getTime()
        : Number.MAX_SAFE_INTEGER
      return aOut - bOut
    })
  }, [current, filter, beforeDate, query])

  const handleSearch = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    setQuery(searchValue.trim())
  }

  const clearSearch = () => {
    setSearchValue('')
    setQuery('')
  }

  const handleCheckOut = (o: IGetAllBedsHiistorySingleResponse) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(checkOut(o.id))
          refetch()
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
        },
        word: 'check out this person',
      }),
    )
  }

  const columns: IHonestTableColumn<IGetAllBedsHiistorySingleResponse>[] = [
    {
      id: 'person',
      header: 'Person',
      minWidth: '11rem',
      cell: (o) => (
        <PersonCell>
          <PersonName>{occupantName(o)}</PersonName>
        </PersonCell>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      minWidth: '6rem',
      cell: (o) => (
        <StatusPill status={o.personType === 1 ? 'info' : 'neutral'}>
          {o.personType === 1 ? 'Employee' : 'Guest'}
        </StatusPill>
      ),
    },
    {
      id: 'bed',
      header: 'Bed',
      minWidth: '6rem',
      cell: (o) => <BedTag>{o.bed?.label || '/'}</BedTag>,
    },
    {
      id: 'location',
      header: 'Location',
      fill: true,
      minWidth: '13rem',
      cell: (o) => (
        <LocationCell>
          <LocationIcon />
          {o.campName || '/'}
          <NextIcon />
          {o.blockName || '/'}
          <NextIcon />
          {o.roomName || '/'}
        </LocationCell>
      ),
    },
    {
      id: 'checkIn',
      header: 'Check-in',
      minWidth: '7rem',
      cell: (o) => <DateCell>{formatDateForDisplay(o.checkInTimestamp)}</DateCell>,
    },
    {
      id: 'expected',
      header: 'Expected check-out',
      minWidth: '9rem',
      cell: (o) => {
        const overdue = isOverdue(o)
        return (
          <div>
            <DateCell $danger={overdue}>
              {o.expectedCheckOutTimestamp ? formatDateForDisplay(o.expectedCheckOutTimestamp) : '/'}
            </DateCell>
            {overdue && <OverdueTag>Overdue {overdueDays(o)}d</OverdueTag>}
          </div>
        )
      },
    },
    {
      id: 'checkout',
      header: '',
      align: 'right',
      minWidth: '8rem',
      cell: (o) => (
        <ActionWrap>
          <Button variant='success' size='sm' onClick={() => handleCheckOut(o)}>
            Check out
          </Button>
        </ActionWrap>
      ),
    },
  ]

  return (
    <>
      <StatBand>
        <MetricCard
          layout='inline'
          label='Currently checked in'
          value={String(current.length)}
          icon={<PersonIcon />}
          accentColor={tokens.status.info.fg}
        />
        <MetricCard
          layout='inline'
          label='Due today'
          value={String(dueTodayCount)}
          icon={<OccupiedBedIcon />}
          accentColor={tokens.accent.base}
        />
        <MetricCard
          layout='inline'
          label='Overdue'
          value={String(overdueCount)}
          icon={<ExclamationMarkIcon />}
          accentColor={overdueCount ? tokens.status.danger.fg : tokens.status.success.fg}
          trailing={overdueCount ? <StatusPill status='danger'>Action</StatusPill> : undefined}
        />
      </StatBand>

      <FilterBar>
        <FilterField>
          <FilterLabel>View</FilterLabel>
          <TabsContainer>
            <Tabs active={filter === 'all'} onClick={() => setFilter('all')}>
              All current
            </Tabs>
            <Tabs active={filter === 'today'} onClick={() => setFilter('today')}>
              Due today
            </Tabs>
            <Tabs active={filter === 'overdue'} onClick={() => setFilter('overdue')}>
              Overdue
            </Tabs>
          </TabsContainer>
        </FilterField>
        <SearchField>
          <Input
            search
            nomarg
            width='300px'
            labelText='Search'
            placeholder='Search by name or bed'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyUp={handleSearch}
            rightIcon={query ? <CloseIcon /> : null}
            onRightIconClick={query ? clearSearch : undefined}
          />
        </SearchField>
        <CalendarInput
          nomarg
          labelText='Expected check-out on/before'
          placeholder='Any date'
          defaultValue={beforeDate}
          onChange={(date: Date | null) => setBeforeDate(date)}
        />
        {beforeDate && (
          <Button variant='secondary' size='sm' onClick={() => setBeforeDate(null)}>
            Clear date
          </Button>
        )}
      </FilterBar>

      {isLoading && !current.length ? (
        <LoaderWrap>
          <JustSpinner />
        </LoaderWrap>
      ) : (
        <Box>
          <HonestTable
            columns={columns}
            data={rows}
            keyField='id'
            emptyMessage={
              current.length
                ? 'No one matches these filters'
                : 'No one is currently checked in'
            }
          />
        </Box>
      )}
    </>
  )
}
